import request from "../../service/request";
import constants from "../constants";
import storageConstants from "../../shared/constants/storage";
import { setCookie } from "../utils/cookie";
import { getLocalStorage, setLocalStorage } from "../../shared/utils/localStorage";
import { getSessionStorage } from "../../shared/utils/sessionStorage";
import { logDTEvent } from "../../shared/analytics/dynatrace";

import { ANALYTICS_STORAGE_KEYS } from "../../shared/constants/analytics";
import { DT_EVENTS, DT_ATTRIBUTES } from "../constants/dynatrace";

const { APP_ENVIRONMENTS, API_BASE_URL, LOGIN_BRANDS, IDENTITY_PROVIDER, LOGIN_METHODS } = constants;
const { FPID, CACHED_ACCOUNT_BRAND, DEVICE_ID } = storageConstants;
const ENV = process.env.NODE_ENV || APP_ENVIRONMENTS.PROD;

/**
 * Get authentication configuration based on app provider settings and account brand
 * @param {Object} appProvider - The application provider object
 * @param {String} accountBrand - The account brand (TELUS/KOODO)
 * @returns {Object} Authentication configuration object containing signInUrl and clientId
 */
const getAuthConfig = (appProvider, accountBrand) => {
  return {
    signInUrl:
      accountBrand === LOGIN_BRANDS.TELUS
        ? appProvider.config.avs_api_urls.sign_in_url
        : appProvider.config.avs_api_urls.sign_in_url_koodo,
    clientId:
      accountBrand === LOGIN_BRANDS.TELUS
        ? appProvider.config.general.AVS_config.clientIdTelus
        : appProvider.config.general.AVS_config.clientIdKoodo,
  };
};

/**
 * Build a sign-in url for the user to sign-in into Telus IAM
 * @param {Object} appProvider
 * @return {String} Sign-In Url
 */
export const getSignInUrl = (appProvider, loginBrand = LOGIN_BRANDS.TELUS, KoodoEnabled = true) => {
  // const baseUrl = "https//watchoptiktv.com/#/";
  // const redirectUri = window.location.href.startsWith("http://localhost")
  //   ? window.location.href.replace("http://localhost:3000/#/", baseUrl)
  //   : window.location.href;
  let redirectUri = appProvider.config.general.AVS_config.redirectUri;
  const accountBrand = loginBrand;
  if (ENV === APP_ENVIRONMENTS.DEV) {
    redirectUri = window.location.origin;
  }
  const { signInUrl, clientId } = getAuthConfig(appProvider, accountBrand);
  let url = signInUrl.replace("$redirectURI", encodeURIComponent(redirectUri));
  url = url.replace("$clientID", clientId);
  url += "&switch_login=" + KoodoEnabled;
  const uuid = getLocalStorage(DEVICE_ID);
  setCookie(FPID, uuid, 365);
  url = url.replace("$state", uuid);
  url = url.replace("$lang", appProvider.lang);

  return url;
};

/**
 * Authenticate a Telus IAM user with the AVS system
 * @param {Object} appProvider
 * @param {String} secret
 * @param {String} state
 * @param {Boolean} isDynatraceTrackingEnabled
 * @return {Promise} Return a promise object
 */
export const authenticateUser = (appProvider, secret, state, isDynatraceTrackingEnabled) => {
  let url = appProvider.config.avs_api_urls.login;
  let redirectUri = appProvider.config.general.AVS_config.redirectUri;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
  url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
    redirectUri = window.location.origin;
  }

  const accountBrand = getLocalStorage(CACHED_ACCOUNT_BRAND) ?? LOGIN_BRANDS.TELUS;

  const { clientId } = getAuthConfig(appProvider, accountBrand);

  const credentialData = {
    credentialsExtAuth: {
      deviceInfo: {
        deviceId: state,
        deviceIdType: "DEVICEID",
        deviceType: appProvider.AVS_PLATFORM,
        brand: accountBrand,
      },
      credentials: {
        client_id: clientId,
        secret: secret,
        state: state,
        redirect_uri: redirectUri,
        idp: IDENTITY_PROVIDER,
      },
    },
  };
  return request(
    {
      url: url,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: credentialData,
      withCredentials: true,
      returnResponseObject: true,
    },
    null,
    appProvider
  )
    .then((response) => {
      if (response?.data?.resultCode !== "KO") {
        // if the login is successful, get the login brand from the response
        const accountBrand =
          response?.data?.resultObj?.profile?.profileData?.customAttributes?.find(
            (item) => item.attributeName === "BRAND"
          )?.attributeValue ?? LOGIN_BRANDS.TELUS;
        setLocalStorage(CACHED_ACCOUNT_BRAND, accountBrand.toLowerCase()); // we cache the user account brand, this can be used to redirect user to brand specific logout url
        // track dynatrace login event
        logDTEvent(
          DT_EVENTS.LOGIN,
          {
            [DT_ATTRIBUTES.USER_BRAND]: accountBrand,
            [DT_ATTRIBUTES.DEVICE_ID]: state,
            [DT_ATTRIBUTES.LOGIN_METHOD]: LOGIN_METHODS.MANUAL,
            [DT_ATTRIBUTES.HAS_ERROR]: false,
            [DT_ATTRIBUTES.TV_ACCOUNT_ID]: response?.data?.resultObj?.profile?.profileData?.crmAccountId,
            [DT_ATTRIBUTES.USER_AVS_SESSION_ID]: getSessionStorage(ANALYTICS_STORAGE_KEYS.SESSION_ID),
            [DT_ATTRIBUTES.USER_PRODUCT_TYPE]:
              response?.data?.resultObj?.profile?.profileData?.properties?.[0]?.propertyName,
          },
          isDynatraceTrackingEnabled
        );
      }
      return response;
    })
    .catch((err) => {
      if (err) {
        // track login errors in dynatrace
        logDTEvent(
          DT_EVENTS.LOGIN,
          {
            [DT_ATTRIBUTES.USER_BRAND]: accountBrand,
            [DT_ATTRIBUTES.DEVICE_ID]: state,
            [DT_ATTRIBUTES.LOGIN_METHOD]: LOGIN_METHODS.MANUAL,
            [DT_ATTRIBUTES.HAS_ERROR]: true,
            [DT_ATTRIBUTES.ERROR_CODE]: `ERR_${err.data?.errorDescription}`,
          },
          isDynatraceTrackingEnabled
        );

        return Promise.reject({
          url: err.config?.url,
          message: err.data?.message,
          type: "Server",
          code: err.data?.errorDescription,
        });
      }
    });
};
